import React, { useRef } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useUserDetailQuery } from '../query/hooks';
import {
  UserPermissionPresetEditor,
  type UserPermissionPresetEditorHandle,
} from './UserPermissionPresetEditor';

type UserPermissionPresetModalProps = {
  jwt: string | null;
  userId: string | null;
  open: boolean;
  onClose: () => void;
};

/** Quick edit: per-user preset as checkboxes grouped by permission group. */
export function UserPermissionPresetModal({ jwt, userId, open, onClose }: UserPermissionPresetModalProps) {
  const editorRef = useRef<UserPermissionPresetEditorHandle | null>(null);
  const { data: user, isPending: userPending } = useUserDetailQuery(jwt, open ? userId : null);

  async function onSave() {
    try {
      await editorRef.current?.save();
      onClose();
    } catch {
      /* surfaced in editor */
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="user-preset-modal-title">
      <DialogTitle id="user-preset-modal-title">Permission preset</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {userPending && <Typography variant="body2">Loading user…</Typography>}
          {!userPending && user && (
            <>
              <Typography variant="body2" color="text.secondary">
                User · {user.username} ({user.email})
              </Typography>
              <Divider />
              <Typography variant="body2" color="text.secondary">
                One preset per user. Toggle accesses below; they are stored on this user’s preset record.
              </Typography>
              <UserPermissionPresetEditor ref={editorRef} jwt={jwt} userId={userId} readOnly={false} />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={() => void onSave()}
          disabled={!userId || !user || userPending}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
